import { Box } from '@mui/material'
import dayjs from 'dayjs'
import { CalMonthChip } from './CalendarApptCard'

/**
 * Desktop month view per the Claude Design "Calendar · five views" — a chip
 * per appointment with a hover peek; click a day number or "+N more" to
 * drill into Day view.
 */

const BORDER_SOFT = '#F1EFF8'
const FONT = "'Outfit', sans-serif"
const CHIP_MAX = 4

export default function CalendarMonthGrid({ anchor, now, visitsForDay, onOpenDay, onVisitClick }) {
	const first = anchor.startOf('month')
	const gridStart = first.subtract(first.day(), 'day')
	const rows = Math.ceil((first.day() + anchor.daysInMonth()) / 7)
	const cells = Array.from({ length: rows * 7 }, (_, i) => gridStart.add(i, 'day'))
	const today = dayjs()

	return (
		<Box sx={{
			bgcolor: '#FFFFFF', border: '1px solid #E7E2F3', borderRadius: '14px',
			boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)', display: 'flex', flexDirection: 'column',
			flex: 1, minHeight: 0, fontFamily: FONT,
		}}>
			<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1.5px solid #E7E2F3', flex: 'none' }}>
				{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
					<Box key={d} component="span" sx={{
						textAlign: 'center', py: '9px', fontSize: '11px', fontWeight: 750,
						letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8B85A4',
					}}>
						{d}
					</Box>
				))}
			</Box>
			<Box sx={{
				display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
				gridTemplateRows: `repeat(${rows}, 1fr)`, flex: 1, minHeight: 0,
			}}>
				{cells.map((d, i) => {
					const inMonth = d.isSame(anchor, 'month')
					const isToday = d.isSame(today, 'day')
					const col = i % 7
					const row = Math.floor(i / 7)
					const visits = visitsForDay(d)
					const extra = visits.length - CHIP_MAX
					return (
						<Box key={d.format('YYYY-MM-DD')} sx={{
							borderLeft: col === 0 ? 'none' : `1px solid ${BORDER_SOFT}`,
							borderTop: row === 0 ? 'none' : `1px solid ${BORDER_SOFT}`,
							p: '6px 7px', display: 'flex', flexDirection: 'column', gap: '3px',
							minWidth: 0, position: 'relative',
							bgcolor: !inMonth ? '#FCFBFE' : (col === 0 || col === 6) ? '#FBFAFD' : 'transparent',
						}}>
							<Box
								component="button"
								type="button"
								title="Open day view"
								onClick={() => onOpenDay(d)}
								sx={{
									width: 24, height: 24, flex: 'none', border: 'none', bgcolor: 'transparent',
									borderRadius: '50%', fontSize: '12px', fontWeight: 750,
									color: !inMonth ? '#C7C0DA' : '#6E6688',
									display: 'flex', alignItems: 'center', justifyContent: 'center',
									cursor: 'pointer', alignSelf: 'flex-start', fontFamily: FONT, p: 0,
									'&:hover': { bgcolor: '#F5F3FB', color: '#5F4EC2' },
									...(isToday ? {
										bgcolor: '#7C6BD9', color: '#FFFFFF',
										boxShadow: '0 2px 6px rgba(124, 107, 217, 0.4)',
										'&:hover': { bgcolor: '#5F4EC2', color: '#FFFFFF' },
									} : {}),
								}}
							>
								{d.date()}
							</Box>
							<Box sx={{
								display: 'flex', flexDirection: 'column', gap: '3px', minHeight: 0,
								flex: '0 1 auto', opacity: inMonth ? 1 : 0.68,
							}}>
								{visits.slice(0, CHIP_MAX).map(v => (
									<CalMonthChip
										key={v.key}
										visit={v}
										now={now}
										side={row >= rows - 2 ? 'above' : 'under'}
										hAlign={col >= 5 ? 'right' : 'left'}
										onClick={() => onVisitClick(v)}
									/>
								))}
							</Box>
							{extra > 0 && (
								<Box
									component="button"
									type="button"
									onClick={() => onOpenDay(d)}
									sx={{
										border: 'none', bgcolor: 'transparent', alignSelf: 'flex-start',
										p: '2px 6px', ml: '18px', borderRadius: '6px', fontSize: '11px',
										fontWeight: 750, color: '#7C6BD9', cursor: 'pointer', fontFamily: FONT,
										'&:hover': { bgcolor: '#EDE9FB', color: '#5F4EC2' },
									}}
								>
									{`+${extra} more`}
								</Box>
							)}
						</Box>
					)
				})}
			</Box>
		</Box>
	)
}
